import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2 } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { useMasterValues } from '../hooks/useMasterData';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import SearchableSelect from '../components/SearchableSelect';

const PAY_STATUSES = ['pending','paid','deducted','waived','partial','disputed'];

export default function Damages() {
  const damageTypes = useMasterValues('damage_type');
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterPay, setFilterPay] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState([]); const [filterDept, setFilterDept] = useState(null);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [empAssets, setEmpAssets] = useState([]);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search; if (filterPay) params.status = filterPay;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      if (filterCompany.length) params.company_id = filterCompany.join(',');
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/damages', { params });
      setRows(r.data.data || r.data.damages || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterPay, dateFrom, dateTo, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
    api.get('/assets', { params: { limit: 2000 } }).then(r => setAssets(r.data.data || [])).catch(() => {});
  }, []);

  const handleEmpSelect = async (empId) => {
    setForm(f => ({ ...f, employee_id: empId, asset_id: '' }));
    setEmpAssets([]);
    if (empId) {
      api.get('/assets', { params: { holder_id: empId, limit: 100 } }).then(r => setEmpAssets(r.data.data || [])).catch(() => {});
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.employee_id) { setError('Employee is required.'); return; }
    setSaving(true); setError('');
    try {
      selected ? await api.put(`/damages/${selected.id}`, form) : await api.post('/damages', form);
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const columns = [
    { header: 'Employee', render: r => <span style={{color:'#e2e8f0'}}>{r.employee?.full_name || r.employee_id || '—'}</span> },
    { header: 'Asset', render: r => <span className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.asset?.asset_number || r.asset_id || '—'}</span> },
    { header: 'Description', render: r => <span className="text-xs truncate max-w-xs block" style={{color:'#94a3b8'}}>{r.description||'—'}</span> },
    { header: 'Repair Cost', render: r => r.repair_cost ? <span style={{color:'#f87171'}}>LKR {Number(r.repair_cost).toLocaleString()}</span> : '—' },
    { header: 'Method', render: r => <span className="text-xs" style={{color:'#475569'}}>{r.payment_method?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())||'—'}</span> },
    { header: 'Status', render: r => <Badge value={r.payment_status}/> },
    { header: 'Date', key: 'incident_date' },
    { header: '', render: r => isIT && <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button> }
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Damage Costs" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
          {isIT && <button onClick={() => { setSelected(null); setForm({payment_status:'pending',payment_method:'company_pay',currency:'LKR',incident_date:new Date().toISOString().split('T')[0]}); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add Damage</button>}
        </div>
      </PageHeader>
      <FilterBar hasFilters={!!(search||filterPay||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterPay(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search employee, asset, description..." />
        <FilterSelect value={filterPay} onChange={v => { setFilterPay(v); setPage(1); }} placeholder="All Payment Statuses" options={PAY_STATUSES.map(s => ({value:s,label:s}))} />
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>
      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage} />
      {modal === 'form' && (
        <Modal title={selected ? 'Edit Damage Record' : 'Add Damage Record'} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Employee *</label>
                <SearchableSelect required value={form.employee_id||''} onChange={v=>handleEmpSelect(v)} placeholder="Select employee..."
                  options={employees.map(e=>({value:e.id,label:`${e.employee_code ? `${e.employee_code} — ` : ''}${e.full_name}`}))}/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>
                  Asset{form.employee_id ? '' : ' (select employee first)'}
                </label>
                <SearchableSelect value={form.asset_id||''} onChange={v=>setForm({...form,asset_id:v})} placeholder={form.employee_id ? "Select asset..." : "Select employee first"}
                  options={(selected ? assets : empAssets).map(a=>({value:a.id,label:`${a.asset_number} — ${a.brand} ${a.model}`}))}/>
              </div>
              <FF label="Incident Date" name="incident_date" type="date" form={form} setForm={setForm} />
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Damage Type</label><select value={form.damage_type||''} onChange={e=>setForm({...form,damage_type:e.target.value})} className="input-dark"><option value="">Select type</option>{damageTypes.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
              <FF label="Repair Cost (LKR)" name="repair_cost" type="number" form={form} setForm={setForm} />
              <FF label="Amount Paid" name="amount_paid" type="number" form={form} setForm={setForm} />
              <FS label="Payment Method" name="payment_method" form={form} setForm={setForm} options={['user_pay','salary_deduction','installment','company_pay'].map(v=>({val:v,label:v.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}))} />
              <FS label="Payment Status" name="payment_status" form={form} setForm={setForm} options={PAY_STATUSES.map(s=>({val:s,label:s}))} />
            </div>
            <FF label="Description" name="description" form={form} setForm={setForm} rows={4} placeholder="Describe the damage incident in detail..." />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
